import React, { useCallback, useEffect, useState, createContext } from 'react'
import { Home } from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useHistory, useParams } from 'react-router-dom'
import _ from 'lodash'
import { useFormik } from 'formik'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import {
  CardBody,
  Card,
  CardTitle,
  Button,
  Nav,
  TabContent,
  TabPane,
  NavLink as NavLinkReactstrap,
  NavItem,
} from 'reactstrap'
import { toast } from 'react-toastify'
import Breadcrumb from '~/components/Breadcrumb'
import { Col, LoadableCard, Show } from '~/components/commons'
import { queries } from '~/constants/mediaQuery'
import { useSelector } from 'react-redux'
import { Alert } from '~/components/Alert'
import useAxios from 'axios-hooks'
import EditLocationForm from './components/EditLocationForm'
import classNames from 'classnames'
import * as Icon from 'react-feather'
import EditHoursForm from './components/EditHoursForm'
import EditServicesTab from './components/EditServicesTab'
// import EditCategoriesForm from './components/EditCategoriesForm'
import EditImagesForm from './components/EditImagesForm'
import LocationPreviewModal from './components/LocationPreviewModal'
import api from '~/services/api'
// import { AdditionalCategoryInput } from '../BulkEdit/EditInformations/PrimaryAdditionalCategories'
import { LOCATION_MASK } from '~/constants/locationMask'
import { ChecklistForm } from './components/ChecklistForm'
import EditVariablesForm from './components/EditVariablesForm'

const editedInitialValues = {
  location: {},
  hours: {},
  categories: {},
  images: {},
}

const NOT_UPDATE_KEYS = ['scheduleEdition', 'name', 'languageCode', 'metadata']

export const EditedFields = createContext()

function generateEditMask(editedFields) {
  const editedMask = Object.values(editedFields).reduce((acc, cur) => {
    const fields = Object.entries(cur)
      .filter(([, value]) => value)
      .map(([key]) =>
        key.match(/categories\/gcid:.*/g)
          ? LOCATION_MASK?.categories
          : LOCATION_MASK?.[key]
      )
      .filter(value => value)
    return [...acc, ...fields.flat()]
  }, [])
  const uniqueEditedMask = Array.from(new Set(editedMask)).join(',')

  return uniqueEditedMask
}

function deepPickBy(obj) {
  const pick = object =>
    _.pickBy(object, item => {
      return !!item || !_.isEmpty(item)
    })

  const res = Object.entries(obj).reduce((acc, [key, value]) => {
    if (_.isObject(value) && !_.isArray(value)) {
      const parsed = pick(value)

      if (Object.keys(parsed)?.length) {
        acc[key] = parsed
      }
    } else if (
      (_.isArray(value) && !!value.length) ||
      (!_.isArray(value) && !!value)
    ) {
      acc[key] = value
    }

    return acc
  }, {})

  _.pickBy(obj, item => {
    return !!item && !_.isEmpty(item)
  })

  return res
}

function parseSpecialHours(hours) {
  return hours.reduce((acc, day) => {
    delete day.tempId
    if (!day.isClosed && (!day.closeTime || !day.openTime)) return acc

    if (day.isClosed) {
      delete day.openTime
      delete day.closeTime
    }
    acc.push(day)
    return acc
  }, [])
}

function compareHour(a, b) {
  if (!a || !b) return

  if (a.hours > b.hours) return 1
  if (a.hours < b.hours) return -1
  return a.minutes === b.minutes ? 0 : a.minutes > b.minutes ? 1 : -1
}

function hasInvalidSpecialHours(specialHours = []) {
  for (const { openTime, closeTime, isClosed } of specialHours) {
    if (isClosed) return false

    if (
      openTime === null ||
      closeTime === null ||
      compareHour(openTime, closeTime) >= 0
    ) {
      return true
    }
  }

  return false
}

CardContainer.Body = styled(CardBody)`
  padding: 2.5rem;
  overflow-x: auto;
  overflow: visible;

  @media ${queries.less.xl} {
    padding: 1.5rem;
  }
`
function CardContainer({ children, title }) {
  return (
    <Card>
      <CardContainer.Body>
        <Show condition={!!title}>
          <CardTitle>
            <FormattedMessage id={title} />
          </CardTitle>
        </Show>
        {children}
      </CardContainer.Body>
    </Card>
  )
}

function LocationSingleEdit() {
  const intl = useIntl()
  const [editedFields, setEditedFields] = useState(editedInitialValues)
  const [updateFields, setUpdateFields] = useState({})
  const [updateMask, setUpdateMask] = useState([])
  const [schedule, setSchedule] = useState(null)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [valid, setValid] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  // \/ setAddedCategories removed for now
  const [addedCategories] = useState(false)
  const [alertsVisible, setAlertsVisible] = useState({
    error: false,
    updated: false,
  })
  const [removeLabels, setRemoveLabels] = useState(false)
  const [locationNames, setLocationNames] = useState([])
  const [activeTab, setActiveTab] = useState('1')
  const [loading, setLoading] = useState(false)
  const [locationVariables, setLocationVariables] = useState({})
  // const [
  //   showBtnAdditionalCategories,
  //   setShowBtnAdditionalCategories,
  // ] = useState(false)
  const [locationCategories, setLocationCategories] = useState([])
  const [locationInitialData, setLocationInitialData] = useState({
    storeCode: '',
    title: '',
    categories: {
      primaryCategory: {
        displayName: '',
        name: '',
      },
      additionalCategories: [],
    },
    websiteUri: '',
    phoneNumbers: {
      primaryPhone: '',
      additionalPhones: [],
    },
    adWordsLocationExtensions: {
      adPhone: '',
    },
    labels: [],
    openInfo: {
      status: '',
      openingDate: '',
    },
    regularHours: {
      periods: [],
    },
    specialHours: {
      specialHourPeriods: [],
    },
    profile: {
      description: '',
    },
    scheduleEdition: '',
    serviceItems: [],
  })

  const { locationId } = useParams()
  const history = useHistory()
  const projectId = useSelector(state => state.user?.selectedProject?.id)
  const accountId = useSelector(state => state.user?.profile?.account?.id)

  const informationsFormik = useFormik({
    initialValues: locationInitialData,
    enableReinitialize: true,
  })

  const hasChangedAny = Object.values({
    ...editedFields,
    images: undefined,
  }).reduce(
    (acc, cur) => Object.values(cur ?? {}).some(field => field) || acc,
    false
  )

  const [, fetchLocation] = useAxios(
    {
      url: `/accounts/${accountId}/locations/${locationId}`,
      method: 'GET',
    },
    {
      manual: true,
    }
  )

  const [, fetchLocationVariables] = useAxios(
    {
      url: `/locations/${locationId}/variables`,
      method: 'GET',
    },
    {
      manual: true,
    }
  )

  const [, fetchCategories] = useAxios('/locations/categories')

  const fillCategoriesData = React.useCallback(async () => {
    const { data } = await fetchCategories()
    const categoryOptions = data.categories
      ?.map(({ name, displayName }) => ({
        label: displayName,
        value: { name: name?.replace('categories/', ''), displayName },
      }))
      .sort(({ label: a }, { label: b }) => a.localeCompare(b))

    setLocationCategories(categoryOptions)
  }, [fetchCategories])

  const breadcrumbPaths = React.useMemo(() => {
    const DEFAULT_PATHS = [
      {
        render: () => (
          <Link className="d-flex align-items-center" to="/">
            <Home size={20} />
          </Link>
        ),
      },
      {
        render: () => (
          <Link className="d-flex align-items-center" to="/locations/overview">
            <FormattedMessage id="Locations" />
          </Link>
        ),
      },
    ]

    const administrativeArea =
      locationInitialData.storefrontAddress?.administrativeArea
    const locality = locationInitialData.storefrontAddress?.locality

    if (!administrativeArea && !locality) {
      return [
        ...DEFAULT_PATHS,
        {
          render: () => (
            <span className="font-medium-3">
              <FormattedMessage id="Edit" />
            </span>
          ),
        },
      ]
    }

    if (administrativeArea) {
      DEFAULT_PATHS.push({
        render: () => (
          <span className="font-medium-3">{administrativeArea}</span>
        ),
      })
    }

    if (locality) {
      DEFAULT_PATHS.push({
        render: () => <span className="font-medium-3">{locality}</span>,
      })
    }

    DEFAULT_PATHS.push({
      render: () => (
        <span className="font-medium-3">{locationInitialData?.title}</span>
      ),
    })

    return DEFAULT_PATHS
  }, [locationInitialData])

  async function handleSubmit() {
    setError('')
    setSubmitting(true)

    const body = {
      update: updateFields,
    }

    const params = {
      updateMask,
      validateOnly: false,
    }

    body.locations = locationNames

    if (schedule) {
      params.scheduleEdition = schedule
    }

    try {
      await api.patch(`/projects/${projectId}/locations`, body, {
        params,
      })

      setAlert(schedule ? 'scheduled' : 'updated', true)
      setSubmitting(false)
      return true
    } catch (err) {
      setAlert('error', true)
      setError('Your locations were NOT updated successfully!')
      setSubmitting(false)
      return false
    }
  }

  async function handleValidateSubmit() {
    const handleError = msg => {
      setValid(false)
      setError(msg)
      setAlert('error', true)
      return false
    }

    setError('')

    const values = deepPickBy(informationsFormik.values)

    const emptyCategories =
      values?.categories?.primaryCategory?.displayName === '' &&
      values?.categories?.primaryCategory?.name === ''

    if (emptyCategories) {
      delete values.categories
    }

    if (removeLabels && !values?.labels?.length) values.labels = []

    if (_.isEmpty(values)) {
      return handleError('You must edit something!')
    }

    if (values?.openInfo?.openingDate) {
      values.openInfo.openingDate = {
        year: values?.openInfo?.openingDate?.getFullYear(),
        month: values?.openInfo?.openingDate?.getMonth() + 1,
        day: values?.openInfo?.openingDate?.getDate(),
      }
    }

    if (!values?.locationNames?.length) {
      return informationsFormik.setErrors({
        locationNames: 'You must select some location to update',
      })
    }

    if (values.scheduleEdition) setSchedule(true)

    setLocationNames(values.locationNames)
    delete values.locationNames

    const update = { ..._.omit(values, NOT_UPDATE_KEYS) }
    const updateMask = generateEditMask(editedFields)

    if (
      editedFields.hours.specialHoursFields &&
      update?.specialHours?.specialHourPeriods.length
    ) {
      if (hasInvalidSpecialHours(update.specialHours.specialHourPeriods)) {
        return handleError('Special hours must be valid!')
      }

      update.specialHours.specialHourPeriods = parseSpecialHours(
        update.specialHours?.specialHourPeriods
      )

      if (!update.specialHours.specialHourPeriods?.length) {
        return handleError('Special hours must be valid!')
      }
    }

    if (update?.regularHours?.periods) {
      update.regularHours.periods = update.regularHours.periods
        .filter(period => period.openTime || period.closeTime)
        .map(period => _.omit(period, 'tempId'))
    }

    try {
      setShowPreviewModal(true)
      await api.patch(
        `/projects/${projectId}/locations/${locationId}`,
        { update, locations: locationNames },
        {
          params: {
            updateMask: updateMask,
            validateOnly: 'true',
          },
        }
      )

      setValid(true)
      setUpdateFields(values)
      setUpdateMask(updateMask)
      toast.success(
        intl.formatMessage({ id: 'The information to be updated is valid!' })
      )

      return true
    } catch (e) {
      console.error(e?.response)
      const message =
        e?.response?.data?.message ||
        `Error validating your location data, try again later.`
      toast.error(message)
      return handleError(message || 'Your locations were NOT updated!')
    }
  }

  function gotoHome() {
    history.push('/')
  }

  function setAlert(name, value) {
    setAlertsVisible(old => ({
      ...old,
      [name]: value,
    }))
  }

  const toggle = () => {
    if (submitting) return
    setShowPreviewModal(prev => !prev)
  }

  const fetchLocationData = useCallback(
    async locationId => {
      setLoading(true)
      fetchLocation({
        url: `/accounts/${accountId}/locations/${locationId}`,
      })
        .then(({ data }) => {
          // set tempId for quickly find on update
          if (data?.regularHours?.periods?.length) {
            data.regularHours.periods = data.regularHours.periods.map(
              period => ({ ...period, tempId: uuidv4() })
            )
          }

          if (data?.openInfo?.openingDate) {
            data.openInfo.openingDate = new Date(
              data?.openInfo?.openingDate?.year,
              data?.openInfo?.openingDate?.month - 1,
              data?.openInfo?.openingDate?.day
            )
          }

          // set tempId for quickly find on update
          if (data?.specialHours?.specialHourPeriods?.length) {
            data.specialHours.specialHourPeriods = data.specialHours.specialHourPeriods.map(
              period => ({ ...period, tempId: uuidv4() })
            )
          }
          setLocationInitialData(prev => ({
            ...prev,
            ...data,
            locationNames: [`locations/${locationId}`],
          }))
        })
        .catch(error => {
          const message =
            error?.response?.data?.message ||
            'Error fetching your location data, try again later.'
          console.error(error?.response)
          toast.error(message)
          history.push('/locations/overview')
        })
        .finally(() => setLoading(false))
    },
    [fetchLocation, accountId, setLoading, setLocationInitialData, history]
  )

  useEffect(() => {
    if (locationId) {
      fetchLocationData(locationId)
      fillCategoriesData()
    } else {
      history.push('/locations/overview')
    }
  }, [locationId, fetchLocationData, fillCategoriesData, history])

  useEffect(() => {
    if (locationId) {
      fetchLocationVariables()
        .then(({ data }) => {
          setLocationVariables(data)
        })
        .catch(error => {
          const message =
            error?.response?.data?.message ||
            'Error fetching your location data, try again later.'
          console.error(error?.response)
          toast.error(message)
        })
    }
  }, [locationId, fetchLocationVariables])

  return (
    <>
      <Col>
        <Breadcrumb paths={breadcrumbPaths} />

        <div className="nav-vertical d-flex flex-column flex-md-row w-100">
          <EditedFields.Provider value={{ editedFields, setEditedFields }}>
            <Nav
              tabs
              className="nav-left mt-1 "
              style={{ minWidth: '150px', boxShadow: 'none' }}
            >
              <NavItem>
                <NavLinkPane
                  className={classNames({
                    active: activeTab === '1',
                  })}
                  onClick={() => setActiveTab('1')}
                >
                  <Icon.MapPin className="mr-1" />
                  <FormattedMessage id="General" />
                </NavLinkPane>
              </NavItem>
              <NavItem>
                <NavLinkPane
                  className={classNames({
                    active: activeTab === '2',
                  })}
                  onClick={() => setActiveTab('2')}
                >
                  <Icon.Clock className="mr-1" />
                  <FormattedMessage id="Horary" />
                </NavLinkPane>
              </NavItem>
              <NavItem>
                <NavLinkPane
                  className={classNames({
                    active: activeTab === '3',
                  })}
                  onClick={() => setActiveTab('3')}
                >
                  <Icon.Briefcase className="mr-1" />
                  <FormattedMessage id="Services" />
                </NavLinkPane>
              </NavItem>
              <NavItem>
                <NavLinkPane
                  className={classNames({
                    active: activeTab === '4',
                  })}
                  onClick={() => setActiveTab('4')}
                >
                  <Icon.Image className="mr-1" />
                  <FormattedMessage id="Images" />
                </NavLinkPane>
              </NavItem>
              <NavItem>
                <NavLinkPane
                  className={classNames({
                    active: activeTab === '5',
                  })}
                  onClick={() => setActiveTab('5')}
                >
                  <Icon.Hash className="mr-1" />
                  <FormattedMessage id="Variables" />
                </NavLinkPane>
              </NavItem>
              <NavItem>
                <NavLinkPane
                  className={classNames({
                    active: activeTab === '6',
                  })}
                  onClick={() => setActiveTab('6')}
                >
                  <Icon.CheckCircle className="mr-1" />
                  <FormattedMessage id="Checklist" />
                </NavLinkPane>
              </NavItem>
            </Nav>
            <TabContent activeTab={activeTab} className="mt-1 w-100">
              <TabPane tabId="1" className="bg-transparent p-1 m--1">
                <LoadableCard className="p-3" loading={loading}>
                  <EditLocationForm
                    formik={informationsFormik}
                    removeLabels={removeLabels}
                    onChangeRemoveLabels={setRemoveLabels}
                    addedCategories={addedCategories}
                    locationCategories={locationCategories}
                  />
                  <Col>
                    <Button
                      disabled={!hasChangedAny}
                      onClick={handleValidateSubmit}
                      color="primary"
                    >
                      <FormattedMessage id="Save changes" />
                    </Button>
                  </Col>
                </LoadableCard>
              </TabPane>
              <TabPane tabId="2" className="bg-transparent">
                <LoadableCard className="p-3" loading={loading}>
                  <EditHoursForm formik={informationsFormik} />
                  <Col>
                    <Button
                      disabled={!hasChangedAny}
                      onClick={handleValidateSubmit}
                      color="primary"
                    >
                      <FormattedMessage id="Save changes" />
                    </Button>
                  </Col>
                </LoadableCard>
              </TabPane>
              <TabPane tabId="3" className="bg-transparent">
                <LoadableCard className="p-3" loading={loading}>
                  <EditServicesTab formik={informationsFormik} />
                </LoadableCard>
              </TabPane>
              <TabPane tabId="4" className="bg-transparent">
                <LoadableCard className="p-3" loading={loading}>
                  <EditImagesForm />
                </LoadableCard>
              </TabPane>
              <TabPane tabId="5" className="bg-transparent">
                <LoadableCard className="p-3" loading={loading}>
                  <EditVariablesForm variables={locationVariables} />
                </LoadableCard>
              </TabPane>
              <TabPane tabId="6" className="bg-transparent">
                <LoadableCard className="p-3" loading={loading}>
                  <ChecklistForm location={locationInitialData} />
                </LoadableCard>
              </TabPane>
            </TabContent>
          </EditedFields.Provider>
        </div>
      </Col>
      <Alert
        type="error"
        visible={alertsVisible.error}
        onClose={() => setAlert('error', false)}
        onCancel={() => setAlert('error', false)}
        confirmBtnText="Try again"
        body={error}
        title="Error"
        closeOnClickOutside
      />
      <Alert
        type="success"
        visible={alertsVisible.updated}
        onClose={gotoHome}
        confirmBtnText="Go to dashboard"
        body="Locations successfully updated!"
        title="Success"
      />
      <LocationPreviewModal
        initialData={locationInitialData}
        data={informationsFormik.values}
        editedFields={editedFields}
        isOpen={showPreviewModal}
        toggle={toggle}
        isValid={valid}
        handleUpdate={handleSubmit}
      />
    </>
  )
}

const NavLinkPane = styled(NavLinkReactstrap)`
  color: #626262 !important;

  &.active {
    font-weight: 600;
    color: #7367f0 !important;
    &::after {
      left: auto !important;
      right: 0px !important;
      top: 1.3rem !important;
    }
  }
`

export default LocationSingleEdit
